/**
 * Jahresabrechnung-Sektion (Phase 6) — kompakt.
 * Liste + Detail-Modal mit Berechnung (Plan/Ist Vergleich, Einheiten-Salden, Vermögensbericht).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Coins, Plus, Calculator, CheckCircle, Bank } from "@phosphor-icons/react";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const contactName = (c) => c ? ([c.first_name, c.last_name].filter(Boolean).join(" ") || c.company || "—") : "—";

export default function AnnualStatementSection({ property }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [detail, setDetail] = useState(null);
  const [calc, setCalc] = useState(null);

  const load = () =>
    api.get(`/weg/${property.id}/annual-statements`)
      .then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, [property.id]);

  const create = async () => {
    try {
      const r = await api.post(`/weg/${property.id}/annual-statements`, {
        property_id: property.id, year: Number(year),
        title: `Jahresabrechnung ${year}`, asset_positions: [],
      });
      toast.success(`Jahresabrechnung ${year} angelegt`);
      setOpen(false); load();
      openDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const openDetail = async (st) => {
    setDetail(st); setCalc(null);
    try {
      const r = await api.post(`/weg/annual-statements/${st.id}/calculate`);
      setCalc(r.data);
    } catch (e) { toast.error("Berechnung fehlgeschlagen"); }
  };

  const approve = async (id) => {
    try { await api.post(`/weg/annual-statements/${id}/approve`); toast.success("Genehmigt"); load(); setDetail(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-3" data-testid="annual-statement-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Coins weight="duotone" className="text-emerald-700" /> Jahresabrechnung
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Vergleich Plan/Ist mit Bank-Buchungen, Einzelabrechnung je Eigentümer, Vermögensbericht.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="annual-add-btn"><Plus size={13} className="mr-1.5" /> Neue Jahresabrechnung</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Neue Jahresabrechnung</DialogTitle></DialogHeader>
            <label className="text-xs text-slate-600 block">Jahr *
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="annual-year-input" />
            </label>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={create} data-testid="annual-create-btn">Anlegen &amp; berechnen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="text-sm text-slate-500">Lade …</div>
        : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50 text-sm text-slate-500" data-testid="annual-empty">
            Noch keine Jahresabrechnung erstellt.
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} data-testid={`annual-item-${r.year}`}>
                <button onClick={() => openDetail(r)} className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:border-emerald-400 transition text-left">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-lg font-semibold text-slate-900">{r.year}</div>
                      <Badge className={`text-[10px] ${r.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {r.status === "draft" ? "Entwurf" : r.status === "approved" ? "Genehmigt" : "Geschlossen"}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.title}</div>
                  </div>
                  <Calculator size={16} className="text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setCalc(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="annual-detail-dialog">
          <DialogHeader>
            <DialogTitle>Jahresabrechnung {detail?.year} <Badge className="ml-2 text-[10px] align-middle">{detail?.status}</Badge></DialogTitle>
          </DialogHeader>
          {!calc ? <div className="text-sm text-slate-500">Berechne …</div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <StatBox label="Plan Ausgaben" value={calc.plan?.expense} />
                <StatBox label="Plan Einnahmen" value={calc.plan?.income} />
                <StatBox label="Vorschüsse Soll" value={calc.vorschuss.soll} />
                <StatBox label="Vorschüsse Ist" value={calc.vorschuss.ist} highlight={calc.vorschuss.offen > 0 ? "warning" : "ok"} />
              </div>
              <div className="text-xs text-slate-600">
                Offener Rückstand: <b className={calc.vorschuss.offen > 0 ? "text-rose-700" : "text-emerald-700"}>{eur(calc.vorschuss.offen)}</b> ·
                Bank-Ist: Einnahmen {eur(calc.ist.bank_income)}, Ausgaben {eur(calc.ist.bank_expense)}
              </div>

              <div>
                <h4 className="text-sm font-medium text-slate-800 mb-2">Einzelabrechnung</h4>
                <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Einheit</th>
                      <th className="text-left px-2 py-1.5">Eigentümer</th>
                      <th className="text-right px-2 py-1.5">Soll</th>
                      <th className="text-right px-2 py-1.5">Ist</th>
                      <th className="text-right px-2 py-1.5">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {calc.per_unit.map((u) => (
                      <tr key={u.unit_id} data-testid={`annual-unit-${u.unit?.unit_number}`}>
                        <td className="px-2 py-1.5 font-mono text-xs">{u.unit?.unit_number}</td>
                        <td className="px-2 py-1.5 text-xs">{contactName(u.owner)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-xs">{eur(u.vorschuss_soll)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-xs">{eur(u.vorschuss_ist)}</td>
                        <td className={`px-2 py-1.5 text-right font-mono text-xs font-semibold ${u.saldo < 0 ? "text-rose-700" : "text-emerald-700"}`}>{eur(u.saldo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {calc.asset_positions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-800 mb-2">Vermögensbericht</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {calc.asset_positions.map((a, i) => (
                      <div key={i} className="rounded border border-slate-200 bg-white p-2 flex items-center justify-between">
                        <div><Bank size={12} className="inline mr-1 text-slate-400" />{a.name}</div>
                        <div className="font-mono font-medium">{eur(a.amount)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detail?.id && (
              <Button variant="outline" data-testid="annual-pdf-btn"
                onClick={() => openAuthenticatedPdf(`/weg/pdf/annual/${detail.id}`)}>
                PDF öffnen
              </Button>
            )}
            {detail?.status === "draft" && (
              <Button onClick={() => approve(detail.id)} data-testid="annual-approve-btn">
                <CheckCircle size={13} className="mr-1.5" /> Genehmigen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  const color = highlight === "warning" ? "border-amber-200 bg-amber-50"
    : highlight === "ok" ? "border-emerald-200 bg-emerald-50"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-3 ${color}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-mono font-semibold mt-1">{eur(value ?? 0)}</div>
    </div>
  );
}
